import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { Button } from '@/components/common/Button';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { ArrowLeft } from 'lucide-react';
import { resolvePostAuthRoute } from '@/lib/authRedirect';

export function LoginOpositorPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const { signInWithEmail, signInWithGoogle } = useAuth();
  const navigate = useNavigate();

  const handleEmailLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      const { error } = await signInWithEmail(email.trim().toLowerCase(), password);
      if (error) {
        if (error.message?.includes('Email not confirmed')) {
          toast.error('Debes verificar tu correo antes de acceder');
          navigate('/auth/verify-required');
        } else {
          toast.error(error.message || 'Error al iniciar sesión');
        }
      } else {
        toast.success('Sesión iniciada');
        // Intent: opositor → siempre va al espacio de oposiciones (preview)
        setTimeout(async () => {
          const { data: { user } } = await supabase.auth.getUser();
          const { data: profileData } = await supabase
            .from('profiles')
            .select('plan, opositor_body')
            .eq('id', user?.id)
            .single();
          navigate(resolvePostAuthRoute({
            plan: profileData?.plan,
            opositorBody: (profileData as any)?.opositor_body,
            intent: 'opositor',
          }));
        }, 400);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    localStorage.setItem('munilex_auth_intent', 'opositor');
    const { error } = await signInWithGoogle();
    if (error) toast.error(error.message || 'Error con Google');
  };

  const goldBtn = 'w-full h-11 rounded-lg font-semibold bg-[hsl(45,85%,55%)] hover:bg-[hsl(45,85%,48%)] text-[hsl(38,80%,15%)] transition-colors disabled:opacity-60';

  return (
    <div>
      <h2 className="text-2xl font-bold mb-1">Acceso Opositores</h2>
      <p className="text-sm text-muted-foreground mb-6">La revolución del aprendizaje para tu plaza</p>

      <form onSubmit={handleEmailLogin} className="space-y-4">
        <div>
          <label className="block text-sm font-semibold mb-1.5">Correo electrónico</label>
          <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required
            className="w-full h-11 px-4 rounded-lg bg-muted border border-border focus:outline-none focus:ring-2 focus:ring-[hsl(45,85%,55%)]"
            placeholder="tu@email.com" />
        </div>
        <div>
          <label className="block text-sm font-semibold mb-1.5">Contraseña</label>
          <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required
            className="w-full h-11 px-4 rounded-lg bg-muted border border-border focus:outline-none focus:ring-2 focus:ring-[hsl(45,85%,55%)]"
            placeholder="••••••••" />
        </div>
        <button type="submit" className={goldBtn} disabled={isLoading}>
          {isLoading ? 'Iniciando sesión...' : 'Iniciar sesión'}
        </button>
      </form>

      <div className="my-6 flex items-center gap-4">
        <div className="flex-1 h-px bg-border" />
        <span className="text-sm text-muted-foreground">o</span>
        <div className="flex-1 h-px bg-border" />
      </div>

      <Button type="button" variant="outline" className="w-full" onClick={handleGoogleLogin}>
        Continuar con Google
      </Button>

      <div className="mt-6 text-center space-y-2">
        <Link to="/auth/opositores/forgot-password" className="block text-sm text-[hsl(38,75%,38%)] hover:underline">
          ¿Has olvidado tu contraseña?
        </Link>
        <p className="text-sm text-muted-foreground">
          ¿No tienes cuenta?{' '}
          <Link to="/auth/opositores/register" className="text-[hsl(38,75%,38%)] hover:underline font-medium">
            Regístrate como opositor
          </Link>
        </p>
      </div>
    </div>
  );
}
